import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useModuleDefinition } from '@platform/modularity/useModuleDefinition';
import { ErrorBanner, Spinner } from '@platform/ui';
import { routes } from '@platform/core/lib/routes';
import { insightPageOf, modulePagesOf } from '@platform/engine/menu/modulePages';
import { mainRecordTypeOf } from '@platform/engine/menu/moduleSections';
import { useWorkspaceParams } from '@platform/hooks/useWorkspaceParams';
import { InsightViewPage } from './InsightViewPage';
import { ModuleAdvisoryPage } from './ModuleAdvisoryPage';
import { ModuleAnalyticsPage } from './ModuleAnalyticsPage';
import { ModuleHelpPage } from './ModuleHelpPage';
import { ModuleOverviewPage } from './ModuleOverviewPage';
import { LawModelPage } from './lawmodel/LawModelPage';
import { PartyCalendarPage } from './pcal/PartyCalendarPage';

/**
 * /:group/:module/:page (сегмент, що не є сутністю модуля, ADR-028) → стандартна сторінка модуля (overview | advisory | analytics | help; `pcal` — календар партії модулів «Партія і
 * група»; `lawmodel` — «Візуальна модель закону» конструктора законопроєктів) або аналітичне подання (insight) з definition.pages.
 */
export function ModulePagesRouter() {
  const { ws, module: moduleKey, page } = useWorkspaceParams();
  const { t } = useTranslation();
  const { data: module, isLoading, error } = useModuleDefinition(moduleKey);
  if (isLoading) return <Spinner />;
  if (error || !module) return <ErrorBanner error={error ?? { message: t('app.notFound') }} />;
  const main = mainRecordTypeOf(module);
  if (!main) return <ErrorBanner error={{ message: 'No record types' }} />;
  const own = modulePagesOf(module).some((p) => p.key === page);
  switch (page) {
    case 'overview': return <ModuleOverviewPage ws={ws} module={module} rt={main} />;
    case 'advisory': return <ModuleAdvisoryPage ws={ws} module={module} rt={main} />;
    case 'analytics': return <ModuleAnalyticsPage ws={ws} module={module} rt={main} />;
    case 'help': return <ModuleHelpPage ws={ws} module={module} rt={main} />;
    case 'pcal': if (own) return <PartyCalendarPage ws={ws} module={module} rt={main} />; break;
    case 'lawmodel': if (own) return <LawModelPage ws={ws} module={module} rt={main} />; break;
  }
  const insight = insightPageOf(module, page);
  if (!insight) return <Navigate to={routes.modulePage(moduleKey, 'overview')} replace />;
  return <InsightViewPage ws={ws} module={module} page={insight} />;
}
